/* =========================
   FILE: src/utils/useAutoLogout.js
   DROP-IN REPLACEMENT
   ========================= */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'pointerdown',
  'wheel'
];

// debounce so mousemove doesn’t spam storage
function throttle(fn, wait = 800) {
  let last = 0;
  return (...args) => {
    const now = Date.now();
    if (now - last >= wait) {
      last = now;
      fn(...args);
    }
  };
}

export function useAutoLogout({
  enabled = true,
  idleMs = 5 * 60 * 1000, // 5 minutes
  warnMs = 60 * 1000, // 1 minute warning
  onLogout
}) {
  const lastActiveKey = useMemo(() => 'vp_last_active_at', []);
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(warnMs / 1000));

  const onLogoutRef = useRef(onLogout);
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  // ✅ prevents onLogout from firing repeatedly every tick
  const firedRef = useRef(false);

  const setLastActive = useCallback(
    (ts) => {
      try {
        localStorage.setItem(lastActiveKey, String(ts));
      } catch {
        // ignore
      }
    },
    [lastActiveKey]
  );

  const getLastActive = useCallback(() => {
    const raw = localStorage.getItem(lastActiveKey);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [lastActiveKey]);

  const markActive = useMemo(
    () =>
      throttle(() => {
        const now = Date.now();
        setLastActive(now);
        setWarningOpen(false);
        firedRef.current = false;
      }, 800),
    [setLastActive]
  );

  // ✅ IMPORTANT: when enabled becomes true (user logs in), reset last-active to NOW
  // This prevents "immediate logout after login" if vp_last_active_at was old.
  useEffect(() => {
    if (!enabled) {
      setWarningOpen(false);
      firedRef.current = false;
      return;
    }
    setLastActive(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // Attach activity listeners
  useEffect(() => {
    if (!enabled) return;

    const handler = () => markActive();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, handler, { passive: true }));

    // treat tab becoming visible/focused as activity
    const vis = () => markActive();
    window.addEventListener('visibilitychange', vis);
    window.addEventListener('focus', vis);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handler));
      window.removeEventListener('visibilitychange', vis);
      window.removeEventListener('focus', vis);
    };
  }, [enabled, markActive]);

  // Main loop: compute based on Date.now (works even when throttled)
  useEffect(() => {
    if (!enabled) {
      setWarningOpen(false);
      return;
    }

    // Safety: ensure warnMs < idleMs (or warning window breaks)
    const safeWarnMs = Math.min(warnMs, Math.max(0, idleMs - 1000));
    const warnAt = idleMs - safeWarnMs; // when warning starts

    const tick = () => {
      const now = Date.now();
      const lastActive = getLastActive() || now;

      const idleFor = now - lastActive;

      // already exceeded idle → logout ONCE
      if (idleFor >= idleMs) {
        setWarningOpen(false);

        if (!firedRef.current) {
          firedRef.current = true;
          onLogoutRef.current?.();
        }
        return;
      }

      // still active enough → allow future logout
      firedRef.current = false;

      // in warning window
      if (idleFor >= warnAt) {
        const remaining = idleMs - idleFor;
        setWarningOpen(true);
        setSecondsLeft(Math.max(1, Math.ceil(remaining / 1000)));
      } else {
        setWarningOpen(false);
      }
    };

    // run once immediately
    tick();

    // heartbeat
    const id = setInterval(tick, 1000);

    // keep tabs in sync
    const onStorage = (e) => {
      if (e.key === lastActiveKey) tick();
      if (e.key === 'token' && !e.newValue) {
        // another tab logged out
        setWarningOpen(false);
        firedRef.current = false;
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      clearInterval(id);
      window.removeEventListener('storage', onStorage);
    };
  }, [enabled, idleMs, warnMs, lastActiveKey, getLastActive]);

  const stayLoggedIn = useCallback(() => {
    const now = Date.now();
    setLastActive(now);
    setWarningOpen(false);
    firedRef.current = false;
  }, [setLastActive]);

  return { warningOpen, secondsLeft, stayLoggedIn };
}
